import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  ArrowLeft,
  Crown,
  User,
  ChevronDown,
  LogOut,
  UserPlus,
  RefreshCw,
  Shield,
  Calendar,
  Mail,
  MoreVertical,
  Key,
  UserMinus,
  Eye,
  EyeOff,
  Edit,
  Send
} from 'lucide-react';

// Using relative URLs
const API = '/api';

const SuperAdminManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [superAdmins, setSuperAdmins] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [showRemoveAccessDialog, setShowRemoveAccessDialog] = useState(false);
  const [showEditProfileDialog, setShowEditProfileDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: '',
    full_name: '',
    password: ''
  });
  const [resetPasswordForm, setResetPasswordForm] = useState({
    new_password: ''
  });
  const [editProfileForm, setEditProfileForm] = useState({
    full_name: '',
    email: ''
  });
  const [confirmationText, setConfirmationText] = useState('');

  useEffect(() => {
    fetchSuperAdmins();
  }, []);

  const fetchSuperAdmins = async () => {
    try {
      const response = await axios.get(`${API}/admin/super-admins`);
      setSuperAdmins(response.data.super_admins || []);
    } catch (error) {
      toast.error('Failed to fetch super admins');
      console.error('Fetch super admins error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSuperAdmin = async () => {
    if (!createForm.email || !createForm.full_name || !createForm.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/create-super-admin`, createForm);
      toast.success(`Super admin ${createForm.email} created successfully`);
      setShowCreateDialog(false);
      setCreateForm({ email: '', full_name: '', password: '' });
      fetchSuperAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create super admin');
      console.error('Create super admin error:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordForm.new_password) {
      toast.error('Please enter a new password');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/reset-super-admin-password`, {
        user_id: selectedUser.id,
        new_password: resetPasswordForm.new_password
      });
      toast.success(`Password reset successfully for ${selectedUser.email}`);
      setShowResetPasswordDialog(false);
      setResetPasswordForm({ new_password: '' });
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
      console.error('Reset password error:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const openRemoveAccessDialog = (targetUser) => {
    if (targetUser.id === user.id) {
      toast.error('Cannot remove your own super admin access');
      return;
    }
    setSelectedUser(targetUser);
    setConfirmationText('');
    setShowRemoveAccessDialog(true);
  };

  const handleRemoveSuperAdmin = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    try {
      await axios.delete(`${API}/admin/remove-super-admin/${selectedUser.id}`);
      toast.success(`Super admin access removed from ${selectedUser.email}`);
      setShowRemoveAccessDialog(false);
      setSelectedUser(null);
      setConfirmationText('');
      fetchSuperAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove super admin');
      console.error('Remove super admin error:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const openEditProfileDialog = (admin) => {
    setSelectedUser(admin);
    setEditProfileForm({
      full_name: admin.full_name || '',
      email: admin.email || ''
    });
    setShowEditProfileDialog(true);
  };

  const handleEditProfile = async () => {
    if (!selectedUser) return;
    
    if (!editProfileForm.full_name.trim()) {
      toast.error('Full name is required');
      return;
    }

    setActionLoading(true);
    try {
      await axios.put(`${API}/admin/update-user-profile/${selectedUser.id}`, {
        full_name: editProfileForm.full_name.trim()
      });
      toast.success('Profile updated successfully');
      setShowEditProfileDialog(false);
      setSelectedUser(null);
      fetchSuperAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
      console.error('Edit profile error:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResendWelcomeEmail = async (admin) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/resend-super-admin-welcome`, {
        user_id: admin.id
      });
      toast.success(`Welcome email resent to ${admin.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend welcome email');
      console.error('Resend welcome email error:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin')}
                className="flex items-center space-x-2"
                data-testid="back-to-admin"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Admin Dashboard</span>
              </Button>
              
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Super Admin Management</h1>
                  <p className="text-sm text-slate-600">Manage super admin users and permissions</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                onClick={fetchSuperAdmins}
                className="border-slate-200 hover:bg-slate-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              
              <Badge className="bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-100 hover:!text-purple-700">
                <Crown className="w-3 h-3 mr-1" />
                Super Admin
              </Badge>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-2 border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                  >
                    <User className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-900 max-w-[150px] truncate">
                      {user?.full_name || user?.email?.split('@')[0]}
                    </span>
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium text-slate-900">{user?.email}</p>
                      <p className="text-xs text-slate-500">Super Administrator</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => {
                      localStorage.clear();
                      window.location.href = '/login';
                    }}
                    className="text-red-600 focus:text-red-600 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Stats & Actions */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-6">
              <Card className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm text-slate-600 mb-2">Total Super Admins</p>
                      <p className="text-3xl font-bold text-purple-600">{superAdmins.length}</p>
                    </div>
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0 ml-6">
                      <Crown className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add Super Admin
            </Button>
          </div>

          {/* Super Admins List */}
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
              <Crown className="w-5 h-5 mr-2 text-purple-600" />
              Super Admin Users ({superAdmins.length})
            </h2>

            {superAdmins.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="p-12 text-center">
                  <Crown className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                  <p className="text-slate-600">No super admins found</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {superAdmins.map((admin) => (
                  <Card key={admin.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                              <Mail className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-slate-900">{admin.email}</h3>
                              <p className="text-sm text-slate-600">{admin.full_name || 'No name provided'}</p>
                            </div>
                            <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-200">
                              Super Admin
                            </Badge>
                            {admin.id === user.id && (
                              <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-200">
                                You
                              </Badge>
                            )}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-13">
                            <div className="flex items-center text-sm text-slate-600">
                              <Calendar className="w-4 h-4 mr-2" />
                              <span>Joined: {formatDate(admin.created_at)}</span>
                            </div>
                            <div className="flex items-center text-sm text-slate-600">
                              <Shield className="w-4 h-4 mr-2" />
                              <span>Super Admin since: {formatDate(admin.created_super_admin_at)}</span>
                            </div>
                          </div>
                        </div>

                        <div className="ml-4">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm" className="border-slate-200 hover:bg-slate-50">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem 
                                onClick={() => openEditProfileDialog(admin)}
                                className="cursor-pointer"
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Profile
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => {
                                  setSelectedUser(admin);
                                  setShowResetPasswordDialog(true);
                                }}
                                className="cursor-pointer"
                              >
                                <Key className="w-4 h-4 mr-2" />
                                Reset Password
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleResendWelcomeEmail(admin)}
                                className="cursor-pointer"
                                disabled={actionLoading}
                              >
                                <Send className="w-4 h-4 mr-2" />
                                Resend Welcome Email
                              </DropdownMenuItem>
                              {admin.id !== user.id && (
                                <DropdownMenuItem 
                                  onClick={() => openRemoveAccessDialog(admin)}
                                  className="cursor-pointer text-red-600 focus:text-red-600"
                                >
                                  <UserMinus className="w-4 h-4 mr-2" />
                                  Remove Access
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Super Admin Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Super Admin</DialogTitle>
            <DialogDescription>
              Create a new super admin user or grant super admin access to an existing user.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="admin@teamnova.ai"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name</Label>
              <Input
                id="full_name"
                value={createForm.full_name}
                onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  placeholder="Enter secure password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                setCreateForm({ email: '', full_name: '', password: '' });
              }}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateSuperAdmin}
              disabled={actionLoading}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {actionLoading ? 'Creating...' : 'Create Super Admin'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={showNewPassword ? "text" : "password"}
                  value={resetPasswordForm.new_password}
                  onChange={(e) => setResetPasswordForm({ new_password: e.target.value })}
                  placeholder="Enter new password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowResetPasswordDialog(false);
                setResetPasswordForm({ new_password: '' });
                setSelectedUser(null);
              }}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={actionLoading}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {actionLoading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Access Dialog */}
      <Dialog open={showRemoveAccessDialog} onOpenChange={setShowRemoveAccessDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Super Admin Access</DialogTitle>
            <DialogDescription>
              This action will permanently remove super admin privileges from {selectedUser?.email}. They will lose access to all administrative functions.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <UserMinus className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-900">This action cannot be undone</p>
                  <p className="text-sm text-red-700 mt-1">
                    {selectedUser?.email} will immediately lose all super admin privileges and will not be able to:
                  </p>
                  <ul className="text-sm text-red-700 mt-2 space-y-1 ml-4 list-disc">
                    <li>Access the super admin dashboard</li>
                    <li>Manage user signups and approvals</li>
                    <li>View customer accounts or audits</li>
                    <li>Perform any administrative functions</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmation">
                To confirm, type <span className="font-mono bg-slate-100 px-1 rounded">REMOVE ACCESS</span> in the box below:
              </Label>
              <Input
                id="confirmation"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder="Type REMOVE ACCESS to confirm"
                className="font-mono"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRemoveAccessDialog(false);
                setSelectedUser(null);
                setConfirmationText('');
              }}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRemoveSuperAdmin}
              disabled={actionLoading || confirmationText !== 'REMOVE ACCESS'}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {actionLoading ? 'Removing Access...' : 'Remove Access'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog open={showEditProfileDialog} onOpenChange={setShowEditProfileDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
            <DialogDescription>
              Update profile information for {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit_email">Email</Label>
              <Input
                id="edit_email"
                value={editProfileForm.email}
                disabled
                className="bg-slate-50"
              />
              <p className="text-xs text-slate-500">Email cannot be changed</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_full_name">Full Name *</Label>
              <Input
                id="edit_full_name"
                value={editProfileForm.full_name}
                onChange={(e) => setEditProfileForm({ ...editProfileForm, full_name: e.target.value })}
                placeholder="Enter full name"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEditProfileDialog(false);
                setSelectedUser(null);
              }}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditProfile}
              disabled={actionLoading || !editProfileForm.full_name.trim()}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {actionLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminManagement;